import type { FileTemplate } from "schemas/generated/fileTemplate";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { fileTemplateRouter } from "@v2/trpc/routes/fileTemplate";

interface UseInfiniteFileTemplates {
  projectId: string | null;
  type?: "page" | "section" | "component";
  category?: string;
  path?: string;
  search?: string;
  limit?: number;
  enabled?: boolean;
  onlyGlobalTemplates?: boolean;
}

interface UseInfiniteFileTemplatesReturn {
  templates: FileTemplate[];
  isLoading: boolean;
  error: unknown;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

/**
 * Custom hook for fetching and managing file template lists with infinite pagination.
 * Provides cursor-based pagination for templates with search functionality.
 * Search is handled by the backend using findRelevant for semantic search.
 *
 * @param options - Configuration options for the templates query
 * @returns Template list data and pagination controls
 */
export function useInfiniteFileTemplates({
  projectId,
  type,
  category,
  path,
  search,
  limit = 20,
  enabled = true,
  onlyGlobalTemplates = false,
}: UseInfiniteFileTemplates): UseInfiniteFileTemplatesReturn {
  const {
    data: templatesData,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = fileTemplateRouter.list.useInfiniteQuery(
    projectId && enabled
      ? {
          projectId,
          type,
          category,
          path,
          search,
          limit,
          onlyGlobalTemplates,
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const templates = React.useMemo(
    () => templatesData?.pages?.flatMap((page) => page.templates) ?? [],
    [templatesData],
  );

  return {
    templates,
    isLoading,
    error,
    fetchNextPage: () => void fetchNextPage(),
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
}
